import React, {
    useState, useContext, useCallback, useEffect, useMemo
} from 'react';
import { claimsMessages } from 'gw-capability-claim-react';
import _ from 'lodash';
import classNames from 'classnames';
import { withRouter, useHistory } from 'react-router-dom';
import { PhoneNumber } from 'gw-components-platform-react';
import moment from 'moment';

import {
    Button,
    InputField,
    Icon,
    DropdownSelectField,
    Link,
    ModalNextProvider
} from '@jutro/components';
import { TranslatorContext, IntlContext } from '@jutro/locale';
import { WizardPage, wizardProps, WizardPageTemplate } from 'gw-portals-wizard-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import { messages as commonMessages } from 'gw-platform-translations';
import { useValidation } from 'gw-portals-validation-react';
import { ViewModelServiceContext, ViewModelForm } from 'gw-portals-viewmodel-react';
import UploadComponent from '../../components/UploadComponent/UploadComponent';
import UploadComponentForSTP from '../../../sasria-capability-fnol-common-react/UploadComponentForSTP/UploadComponentForSTP';
import metadata from './AdditionalInformationPage.metadata.json5';
import styles from './AdditionalInformationPage.module.scss';
import messages from '../../FNOL.messages';
import Claim from '../../models/Claim';
import {
    validatePolicyScheduleDate
} from 'sasria-capability-fnol-common-react/Validations/ValidationUtil';
import { truncate } from 'lodash';
import { findDefaultValues } from '@jutro/uimetadata';

const ROLES_TO_FILTER = ['witness', 'other'];

const getPhoneFieldBasedOnPrimaryPhoneType = (relatedContact) => {
    let primaryPhoneType;
    switch (relatedContact.contact.primaryPhoneType) {
        case 'home':
            primaryPhoneType = 'homeNumber';
            break;
        case 'work':
            primaryPhoneType = 'workNumber';
            break;
        case 'mobile':
            primaryPhoneType = 'cellNumber';
            break;
        default:
            primaryPhoneType = 'homeNumber';
    }
    return primaryPhoneType;
};

function FNOLAdditionalInformationPage(props) {
    const {
        wizardData: claimVM, updateWizardData, authHeader,
        history: {
            location: { state }
        }
    } = props;
    const history = useHistory();

    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const intl = useContext(IntlContext);
    const [claimObj, updateClaimObj] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');

    const { FNOLService } = useDependencies('FNOLService');
    const { onValidate, isComponentValid, registerComponentValidation } = useValidation(
        'FNOLAdditionalInformationPage'
    );

    const showInjuredColumn = useMemo(
        () => !['InlandMarine', 'GeneralLiability', 'BusinessOwners'].includes(_.get(claimVM, 'policy.policyType.value')),
        // Policy type won't change on re-renders
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const writeValue = useCallback(
        (value, path) => {
            _.set(claimVM, path, value);
            updateWizardData(claimVM);
            _.set(claimObj, 'relatedContacts', claimVM.relatedContacts.value);
        },
        [claimObj, claimVM, updateWizardData]
    );

    useEffect(() => {
        updateClaimObj(new Claim(claimVM.value));
        // execute once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onNext = useCallback(() => {
        if (claimVM.value.sasriaExpressClaimDetails_EXT.isExpressClaimSelected) {
            const policyEffectiveDate = moment(_.get(claimVM.value, 'policy.effectiveDate'));
            const policyExpiryDate = moment(_.get(claimVM.value, 'policy.expirationDate'));
            policyEffectiveDate.set(
                {
                    hour: 0, minute: 0, second: 0, millisecond: 0
                }
            );
            policyExpiryDate.set(
                {
                    hour: 0, minute: 0, second: 0, millisecond: 0
                }
            );
            const lossDate = moment(_.get(claimVM.value, 'lossDate'))
            // Get all the express claim data and show warning if validation fails.
            const docEffectiveDate = _.get(claimVM.value, 'sasriaExpressClaimDetails_EXT.policyInceptionDate');
            const docExpiryDate = _.get(claimVM.value, 'sasriaExpressClaimDetails_EXT.policyExpiryDate');

            const lastPremiumPaidDate = _.get(claimVM.value, 'sasriaExpressClaimDetails_EXT.lastPremiumPaidDate');
            const premiumPaidAmount = _.get(claimVM.value, 'sasriaExpressClaimDetails_EXT.premiumPaidAmount');
            const annualOrMonthlyPremium = _.get(claimVM.value, 'sasriaExpressClaimDetails_EXT.annualOrMonthlyPremium');
            const noOfPhotoAdded = _.get(claimVM.value, 'sasriaExpressClaimDetails_EXT.noOfPhotoAdded');
            const errorMessageArray = [];
            const errorMessage1 = 'Not all required documents have been uploaded or not all fields have been captured!';
            errorMessageArray.push(errorMessage1);
            const errorMessage2 = 'For Fastest processing of your claims please complete full requirements.';
            errorMessageArray.push(errorMessage2);
            errorMessageArray.push('');

            errorMessageArray.push('Fields Missing:');
            let checkIfFieldMissing = false;
            // Check if fields are empty.
            if (docEffectiveDate === undefined || docEffectiveDate === null) {
                errorMessageArray.push('Policy Inception Date');
                checkIfFieldMissing = true;
            }
            if (docExpiryDate === undefined || docExpiryDate === null) {
                errorMessageArray.push('Policy Expiry Date');
                checkIfFieldMissing = true;
            }
            if (lastPremiumPaidDate === undefined || lastPremiumPaidDate === null) {
                errorMessageArray.push('Premium Paid Date');
                checkIfFieldMissing = true;
            }
            if (premiumPaidAmount === undefined || premiumPaidAmount === null || premiumPaidAmount === '') {
                errorMessageArray.push('Premium Amount');
                checkIfFieldMissing = true;
            }
            if (annualOrMonthlyPremium === undefined || annualOrMonthlyPremium === null) {
                errorMessageArray.push('Annual/Monthly Premium');
                checkIfFieldMissing = true;
            }
            if (noOfPhotoAdded === undefined || noOfPhotoAdded === null) {
                errorMessageArray.push("Number Photograph's");
                checkIfFieldMissing = true;
            }
            if (!checkIfFieldMissing) {
                const indexToDelete = errorMessageArray.indexOf('Fields Missing:');
                errorMessageArray.splice(indexToDelete, 1);
            }

            // Validate Document Outstanding
            const documentsArray = _.get(claimVM, 'documents.value') || [];

            const premiumCountList = documentsArray.filter(function (document) {
                return document.sasriaFormName_EXT === 'Premium';
            });
            const claimFormCountList = documentsArray.filter(function (document) {
                return document.sasriaFormName_EXT === 'Claim Form';
            });
            const policyScheduleCountList = documentsArray.filter(function (document) {
                return document.sasriaFormName_EXT === 'Policy Schedule';
            });
            var photoCountList = documentsArray.filter(function (document) {
                return document.sasriaFormName_EXT === 'Photos';
            });

            let checkDocumentNotUploaded = false;
            if (premiumCountList.length < 1 || claimFormCountList.length < 1
                || policyScheduleCountList.length < 1 || photoCountList.length < 1) {
                checkDocumentNotUploaded = true;
                errorMessageArray.push('');
                errorMessageArray.push('Document Outstanding:');
            }

            if (premiumCountList.length < 1) {
                errorMessageArray.push('Proof Of Premium');
            }

            if (claimFormCountList.length < 1) {
                errorMessageArray.push('Claim Form');
            }

            if (policyScheduleCountList.length < 1) {
                errorMessageArray.push('Policy Schedule');
            }

            if (photoCountList.length < 1) {
                errorMessageArray.push('Photos');
            }

            errorMessageArray.push('');
            errorMessageArray.push('Validation Failed:');

            let checkFieldValidationFailed = false;
            _.set(claimVM, 'policySchValidationFailed_EXT.value', false);
            if (docEffectiveDate !== undefined && docExpiryDate !== undefined) {
                if (!(policyEffectiveDate.isSame(moment(docEffectiveDate)) && policyExpiryDate.isSame(moment(docExpiryDate)))) {
                    checkFieldValidationFailed = true;
                    _.set(claimVM, 'policySchValidationFailed_EXT.value', true);
                    errorMessageArray.push('Policy Schedule Dates do not match dates entered at start of claim');
                }
            }

            if (noOfPhotoAdded === undefined || noOfPhotoAdded < 3) {
                checkFieldValidationFailed = true;
                errorMessageArray.push('Minimum 3 photos need to be uploaded');
            }
            if (premiumPaidAmount !== undefined && premiumPaidAmount !== '' && !Number(premiumPaidAmount)) {
                checkFieldValidationFailed = true;
                errorMessageArray.push('Premium Amount Should be a Numeric value');
            }
            if (lastPremiumPaidDate !== undefined) {
                if (!(moment(lastPremiumPaidDate).isSameOrAfter(policyEffectiveDate)
                    && (moment(lastPremiumPaidDate).isSameOrBefore(policyExpiryDate)))) {
                    checkFieldValidationFailed = true;
                    errorMessageArray.push('Premium Paid Date is outside of policy schedule dates');
                }
                if (moment(lastPremiumPaidDate).isAfter(lossDate)) {
                    checkFieldValidationFailed = true;
                    errorMessageArray.push('Premium Paid Date should not be after loss date');
                }
            }
            if (!checkFieldValidationFailed) {
                const indexToDelete = errorMessageArray.indexOf('Validation Failed:');
                errorMessageArray.splice(indexToDelete, 1);
            }

            errorMessageArray.push('');
            errorMessageArray.push('Select \'No\' to correct.');
            errorMessageArray.push('Select \'Yes\' flag claim and continue.');

            if (checkIfFieldMissing || checkDocumentNotUploaded || checkFieldValidationFailed) {
                _.set(claimVM, 'isThisClaimFlagged_EXT.value', true);
                return ModalNextProvider.showConfirm({
                    title: 'Warning',
                    message: errorMessageArray.join('\n'),
                    status: 'warning',
                    icon: 'mi-error-outline',
                    confirmButtonText: claimsMessages.removeConfirmationYes,
                    cancelButtonText: claimsMessages.removeConfirmationNo
                }).then((results) => {
                    if (results === 'cancel') {
                        return false;
                    }
                    return claimVM;
                });
            } else {
                _.set(claimVM, 'isThisClaimFlagged_EXT.value', false);
            }
        }
        return claimVM;
    }, [claimVM]);

    const getFirstLastNameData = useCallback(
        (item, rowId, property) => {
            const { id, path } = property;
            const claimVMPath = `relatedContacts.value[${rowId}].${path}`;
            const setDisable = item.contact.publicID
                ? 'disabled'
                : '';
            return (
                <InputField
                    id={`${id}_${rowId}`}
                    className={styles.tableColumn}
                    path={claimVMPath}
                    onValueChange={writeValue}
                    value={_.get(claimVM, claimVMPath)}
                    disabled={setDisable}
                />
            );
        },
        [claimVM, writeValue]
    );

    const filteredRoleTypeList = useMemo(() => {
        const typeList = viewModelService.create(
            {},
            'cc',
            'edge.capabilities.claim.fnol.dto.FnolRelatedContactDTO'
        );
        const filteredList = typeList.role.aspects.availableValues
            .filter((typeCode) => {
                return ROLES_TO_FILTER.includes(typeCode.code);
            })
            .map((typeCode) => {
                return {
                    code: typeCode.code,
                    name: translator({
                        id: typeCode.name,
                        defaultMessage: typeCode.name
                    })
                };
            });
        return filteredList;
    }, [translator, viewModelService]);

    const getInvolvementInjuredData = useCallback(
        (item, rowId, property) => {
            const { id, path } = property;
            let availableValues;
            const claimVMPath = `relatedContacts.value[${rowId}].${path}`;
            if (id === 'role') {
                availableValues = filteredRoleTypeList;
            } else {
                availableValues = [
                    {
                        code: 'true',
                        name: translator(messages.fnolYes)
                    },
                    {
                        code: 'false',
                        name: translator(messages.fnolNo)
                    }
                ];
            }
            return (
                <DropdownSelectField
                    id={`${id}_${rowId}`}
                    alwaysShowPlaceholder={false}
                    availableValues={availableValues}
                    path={claimVMPath}
                    onValueChange={writeValue}
                    value={_.get(claimVM, claimVMPath)}
                    className={styles.involvmentInjuredDropDown}
                />
            );
        },
        [claimVM, filteredRoleTypeList, translator, writeValue]
    );

    const getPhoneNumberData = useCallback(
        (item, rowId) => {
            const phoneField = getPhoneFieldBasedOnPrimaryPhoneType(item);
            const claimVMPath = `relatedContacts.value[${rowId}].contact.${phoneField}`;
            const setDisable = item.contact.publicID
                ? 'disabled'
                : '';
            return (
                <PhoneNumber
                    id={`${item.primaryPhoneID}_${rowId}`}
                    alwaysShowMask
                    onValueChange={writeValue}
                    path={claimVMPath}
                    disabled={setDisable}
                    value={_.get(claimVM, claimVMPath)}
                />
            );
        },
        [claimVM, writeValue]
    );

    const getContactsRelatedPersons = useCallback((contactsItem) => {
        if (contactsItem.subtype === 'Person' && contactsItem.lastName) {
            return `${contactsItem.firstName} ${contactsItem.lastName}`;
        }
        return '';
    }, []);

    const generateMoreInfoAddPersonOverrides = useCallback(() => {
        const contactsPath = 'value.contacts';
        const contactsItems = _.get(claimVM, contactsPath, []);
        let availableRelatedContacts = [];
        if (!_.isEmpty(claimObj)) {
            availableRelatedContacts = claimObj.availableRelatedContacts().filter((contact) => {
                return contact.subtype === 'Person' && contact.firstName;
            });
        }

        if (!contactsItems.length) {
            return {};
        }
        const overrides = contactsItems.map((contactItem, index) => {
            return {
                [`contactItems${index}`]: {
                    message: getContactsRelatedPersons(contactItem)
                },
                [`contactItemsDropDown${index}`]: {
                    visible: _.includes(availableRelatedContacts, contactItem)
                }
            };
        });
        return Object.assign({}, ...overrides);
    }, [claimVM, claimObj, getContactsRelatedPersons]);

    const removeContactItem = useCallback(
        (e, rowId) => {
            e.preventDefault();
            const { relatedContacts } = claimVM;
            const relatedContactToDelete = relatedContacts.value.filter((relatedContact, index) => {
                return index === rowId;
            });
            claimObj.removeRelatedContact(relatedContactToDelete[0].contact);
            _.set(claimVM.value, 'relatedContacts', claimObj.relatedContacts);
            updateWizardData(claimVM);
        },
        [claimVM, claimObj, updateWizardData]
    );

    const removeContactRow = useCallback(
        (item, index) => {
            return (
                <Link
                    to="/"
                    onClick={(e) => removeContactItem(e, index, item)}
                    className={classNames(styles.deleteSection, styles.deleteRow)}
                >
                    <Icon icon="mi-delete" className={styles.deleteIcon} />
                </Link>
            );
        },
        [removeContactItem]
    );

    const generateNewWitnessRow = useCallback(
        (event) => {
            event.preventDefault();
            const { contacts } = claimVM;
            const getCurrentMenuItemID = event.currentTarget.id;
            if (getCurrentMenuItemID.indexOf('anotherPersonLink') === -1) {
                const getIndex = parseInt(getCurrentMenuItemID.match(/[0-9]/gi), 10);
                const selectedContact = contacts.value[getIndex];
                claimObj.addRelatedContact(selectedContact);
                _.set(claimVM.value, 'relatedContacts', claimObj.relatedContacts);
            } else {
                claimObj.addRelatedContact(null);
                _.set(claimVM.value, 'relatedContacts', claimObj.relatedContacts);
            }
            updateWizardData(claimVM);
        },
        [claimVM, updateWizardData, claimObj]
    );

    const generateAddPersonMenuDropDown = useCallback(
        (dropDownProps, toggleMenu) => {
            const { isOpen } = dropDownProps;
            const onAddPersonClick = () => {
                toggleMenu(!isOpen);
            };
            return (
                <Button onClick={onAddPersonClick} icon="mi-expand_more" iconPosition="right">
                    {translator(messages.fnolAddPerson)}
                </Button>
            );
        },
        [translator]
    );

    const validateTableForm = useCallback(() => {
        if (
            !claimVM.relatedContacts.aspects.valid
            || !claimVM.relatedContacts.aspects.subtreeValid
        ) {
            return false;
        }
        return true;
    }, [claimVM]);

    const overrides = {
        witnessPartyTable: {
            data: claimVM.relatedContacts.value,
            visible: !_.isEmpty(claimVM.relatedContacts.value)
        },
        injuredHeader: {
            visible: showInjuredColumn
        },
        documentUpload: {
            path: !_.isUndefined(claimVM) ? claimVM : null,
            updatePath: updateWizardData,
            visible: !claimVM.value.sasriaExpressClaimDetails_EXT.isExpressClaimSelected
        },
        expressClaimSTP: {
            value: claimVM.value.sasriaExpressClaimDetails_EXT.isExpressClaimSelected,
        },
        documentUploadForSTP: {
            path: !_.isUndefined(claimVM) ? claimVM : null,
            updatePath: updateWizardData,
            visible: claimVM.value.sasriaExpressClaimDetails_EXT.isExpressClaimSelected,
            docListForSpecificSteps: claimVM.value.documentCheckList_EXT,
            isPhotoUpload: false
        },
        ...generateMoreInfoAddPersonOverrides()
    };
    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            generateWitnessPartyTableRow: generateNewWitnessRow,
            getFirstNameData: getFirstLastNameData,
            getLastNameData: getFirstLastNameData,
            getInvolvementData: getInvolvementInjuredData,
            getPhoneNumberData: getPhoneNumberData,
            getInjuredData: getInvolvementInjuredData,
            removeContactRow: removeContactRow,
            renderTrigger: generateAddPersonMenuDropDown
        },
        resolveComponentMap: {
            uploadcomponent: UploadComponent,
            uploadComponentforSTP: UploadComponentForSTP

        }
    };

    useEffect(() => {
        registerComponentValidation(validateTableForm);
    }, [registerComponentValidation, validateTableForm]);

    return (
        <WizardPage
            // nextLabel={translator(messages.fnolSubmitClaim)}
            onNext={onNext}
            cancelLabel={translator(messages.fnolCancel)}
            disableNext={!isComponentValid}
            template={WizardPageTemplate}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={claimVM}
                overrideProps={overrides}
                onModelChange={updateWizardData}
                onValueChange={writeValue}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                componentMap={resolvers.resolveComponentMap}
            />
        </WizardPage>
    );
}
FNOLAdditionalInformationPage.propTypes = wizardProps;
export default withRouter(withAuthenticationContext(FNOLAdditionalInformationPage));
